import { type ReactNode, useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { IconClose } from "../../components/IceBlueGlyphs";
import { acquireBodyScrollLock } from "../../lib/bodyScrollLock";
import "./schedule-modal.css";

const FOCUSABLE =
  'button:not([disabled]),a[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex="0"]';

/**
 * Schedule's Ice Blue dialog (`.schedule-modal-*` in schedule-modal.css, `--schedule-*` tokens). It is
 * portaled over the page with the rest of the app inert. Focus starts on `[data-autofocus]` (else
 * the first control) and stays inside; Escape and the close button leave without saving; focus
 * returns to the opener. The scrim does not close it, so a half-filled form is never lost to a
 * stray click. `size="wide"` (about 960px, columns stack below 620px) is for side-by-side review,
 * such as the intake review. `className` adds a surface's own class to the dialog (Today's walk-in
 * dialog uses `tb-walk-in-modal`).
 */
export function ScheduleModal({
  title,
  eyebrow,
  description,
  size = "default",
  className,
  onEscape,
  onClose,
  children,
}: {
  readonly title: string;
  readonly eyebrow?: string;
  readonly description?: ReactNode;
  readonly size?: "default" | "wide";
  readonly className?: string | undefined;
  /**
   * Runs first on Escape. Return true when the key was used inside the dialog (such as closing a
   * combobox's list), so the dialog stays open.
   */
  readonly onEscape?: (() => boolean) | undefined;
  readonly onClose: () => void;
  readonly children: ReactNode;
}) {
  const titleId = useId();
  const descriptionId = useId();
  const layer = useRef<HTMLDivElement>(null);
  const dialog = useRef<HTMLElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  const escapeFirst = useRef(onEscape);
  escapeFirst.current = onEscape;

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const siblings = Array.from(document.body.children).filter(
      (node): node is HTMLElement => node instanceof HTMLElement && node !== layer.current,
    );
    const previous = siblings.map((node) => node.inert);
    siblings.forEach((node) => {
      node.inert = true;
    });
    const release = acquireBodyScrollLock();
    const controls = () =>
      Array.from(dialog.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []).filter(
        (node) => !node.closest("[hidden]"),
      );
    (dialog.current?.querySelector<HTMLElement>("[data-autofocus]") ?? controls()[0])?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (escapeFirst.current?.() === true) return;
        close.current();
      } else if (event.key === "Tab") {
        const focusable = controls();
        const first = focusable[0];
        const last = focusable.at(-1);
        if (
          first !== undefined &&
          last !== undefined &&
          (!dialog.current?.contains(document.activeElement) ||
            (event.shiftKey ? document.activeElement === first : document.activeElement === last))
        ) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
        }
      } else if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        // The command palette would open under the scrim and take focus out of the dialog.
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    window.addEventListener("keydown", keydown, true);
    return () => {
      window.removeEventListener("keydown", keydown, true);
      siblings.forEach((node, index) => {
        node.inert = previous[index] ?? false;
      });
      release();
      if (opener?.isConnected) opener.focus();
    };
  }, []);

  return createPortal(
    <div className="schedule-modal-scrim" ref={layer}>
      <section
        ref={dialog}
        className={["schedule-modal", size === "wide" ? "wide" : undefined, className]
          .filter((part) => part !== undefined && part !== "")
          .join(" ")}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description === undefined ? undefined : descriptionId}
      >
        <header className="schedule-modal-head">
          <div>
            {eyebrow === undefined ? null : <p className="schedule-modal-eyebrow">{eyebrow}</p>}
            <h2 id={titleId}>{title}</h2>
          </div>
          <button
            type="button"
            className="schedule-modal-close"
            aria-label={`Close ${title}`}
            onClick={onClose}
          >
            <IconClose />
          </button>
        </header>
        {description === undefined ? null : (
          <p id={descriptionId} className="schedule-modal-intro">
            {description}
          </p>
        )}
        {children}
      </section>
    </div>,
    document.body,
  );
}
